import {skipToken} from '@reduxjs/toolkit/query';
import {
  Action,
  Card,
  closeCurrentDialog,
  openDialog,
  PhoneInput,
  PhoneNumber,
  showNotification,
  TextInput,
} from 'platform/components';
import {Grid, Hide, VStack} from 'platform/foundation';

import {useDispatch} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {metadaApi} from '@dms/api/base';
import {CustomerResponseBodyV2, useGetCustomerV2Query} from '@dms/api/customer';
import {useGetVehicleQuery} from '@dms/api/sales';
import {GetMetadaServiceCaseApiResponse, usePatchServiceCaseMutation} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  CustomerMatchOrCreate,
  CustomerSelectSearch,
  getPhoneNumber,
  getSaneCustomerContactIds,
  handleApiError,
  isInvalidEmail,
  SaneCustomer,
  SaneCustomerContactIds,
  useEditCustomer,
  usePhoneNumbers,
} from '@dms/shared';

import {buildArray, suffixTestId, TestIdProps} from 'shared/web';

interface ContactPersonCardProps extends TestIdProps {
  serviceCaseId: string;
  serviceCase: GetMetadaServiceCaseApiResponse;
  isReadOnly?: boolean;
}

export function ContactPersonCard(props: ContactPersonCardProps) {
  const dispatch = useDispatch();
  const {countriesOptions} = usePhoneNumbers();

  const serviceCase = props.serviceCase;
  const contactInformation = serviceCase?.contactInformation;

  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery(
    isNotNil(serviceCase?.vehicleId) ? {vehicleId: serviceCase.vehicleId} : skipToken
  );

  const {data: contactCustomer} = useGetCustomerV2Query(
    isNotNil(contactInformation?.customerId)
      ? {customerId: contactInformation.customerId}
      : skipToken
  );

  const [patchServiceCase, {isLoading: isPatching}] = usePatchServiceCaseMutation();

  const editCustomer = useEditCustomer(contactCustomer, () => {
    dispatch(metadaApi.util.invalidateTags([{type: 'serviceCase', id: props.serviceCaseId}]));
  });

  if (isNil(serviceCase)) {
    return null;
  }

  const saveContactInformation = (contactIds: SaneCustomerContactIds) => {
    const vehicleId = serviceCase.vehicleId;
    const customerId = serviceCase.customerId;

    if (isNil(vehicleId) || isNil(customerId) || isVehicleLoading || isVehicleError) {
      return;
    }

    patchServiceCase({
      serviceCaseId: props.serviceCaseId,
      body: {
        vehicleId,
        customerId,
        make: vehicle?.make ?? null,
        modelFamily: vehicle?.modelFamily ?? null,
        type: vehicle?.type ?? null,
        vin: vehicle?.vin,
        registrationPlate: vehicle?.vehicle.registrationPlate,
        contactInformation: contactIds,
      },
    })
      .unwrap()
      .then(() =>
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'))
      )
      .catch(handleApiError);
  };

  const handleCustomerSelect = (customer: SaneCustomer) =>
    saveContactInformation(getSaneCustomerContactIds(customer));

  const handleCustomerCreated = (customer: CustomerResponseBodyV2) => {
    closeCurrentDialog();
    saveContactInformation({
      customerId: customer.id,
      contactId: customer.primaryContactId ?? null,
    });
  };

  const handleCreateCustomer = () =>
    openDialog(
      <CustomerMatchOrCreate
        onCustomer={handleCustomerCreated}
        data-testid={suffixTestId('createCustomer', props)}
      />,
      {title: i18n.t('entity.customer.labels.customer')}
    );

  const actions = buildArray<Action>()
    .whenNot(props.isReadOnly, {
      type: 'button',
      variant: 'link',
      leftIcon: 'image/edit',
      title: i18n.t('entity.customer.actions.editCustomer'),
      onClick: () => editCustomer?.(),
    })
    .whenNot(props.isReadOnly, {
      type: 'button',
      variant: 'link',
      leftIcon: 'content/add_circle',
      title: i18n.t('general.labels.createNew'),
      onClick: handleCreateCustomer,
    });

  const formattedPhoneNumber = getPhoneNumber(
    contactInformation?.phoneNumber?.prefix,
    contactInformation?.phoneNumber?.number,
    countriesOptions
  );

  const phoneValue: PhoneNumber = {
    countryCode: formattedPhoneNumber.countryCode || 'CZE',
    number: formattedPhoneNumber.number || '',
    prefix: formattedPhoneNumber.prefix || '',
  };

  return (
    <Card
      title={i18n.t('entity.customer.labels.contactPerson')}
      actions={actions}
      data-testid={suffixTestId('contactPersonCard', props)}
    >
      <VStack spacing={4}>
        <Hide when={props.isReadOnly}>
          <CustomerSelectSearch
            onCustomerSelect={handleCustomerSelect}
            isDisabled={isVehicleLoading || isVehicleError || isPatching}
            data-testid={suffixTestId('search', props)}
          />
        </Hide>
        <Grid columns={[1, 1, 3, 3]} spacing={4}>
          <TextInput
            label={i18n.t('general.labels.name')}
            value={contactInformation?.name ?? null}
            isDisabled
            data-testid={suffixTestId('name', props)}
          />
          <TextInput
            label={i18n.t('general.labels.emailAddress')}
            value={contactInformation?.email ?? null}
            errorMessage={
              isInvalidEmail(contactInformation?.email)
                ? i18n.t('general.validations.invalidEmail')
                : undefined
            }
            isDisabled
            data-testid={suffixTestId('email', props)}
          />
          <PhoneInput
            label={i18n.t('entity.phoneNumber.labels.number')}
            value={phoneValue}
            countries={countriesOptions}
            isDisabled
            data-testid={suffixTestId('phoneNumber', props)}
          />
        </Grid>
      </VStack>
    </Card>
  );
}
